/**
 * Footage upload: drag-and-drop + file picker, real progress, and the safeguarding consent
 * gate (implementation-plan.md §3.3.1 — a v1 OBLIGATION, not optional).
 *
 * The gate is enforced twice on purpose: here (so the coach gets an explanation) and again in
 * callout-api before anything reaches R2 (so a crafted request cannot bypass it).
 */
import { useCallback, useRef, useState } from 'react';
import {
  FOOTAGE_MAX_BYTES,
  checkFootageFile,
  formatBytes,
  formatClock,
  planFootageUpload,
  probeVideoMetadata,
  type VideoMetadata,
} from './upload';
import {
  uploadFootage,
  uploadFootageMultipart,
  type FootageAsset,
  type SignedMedia,
} from '../api/client';

type Phase = 'idle' | 'probing' | 'uploading' | 'ready' | 'error';

export interface VideoUploadProps {
  onUploaded: (footage: FootageAsset, media: SignedMedia) => void;
  getToken: () => Promise<string | null>;
}

export function VideoUpload({ onUploaded, getToken }: VideoUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
  const [containsMinors, setContainsMinors] = useState<boolean | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [footage, setFootage] = useState<FootageAsset | null>(null);

  const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  const gateBlocked = containsMinors === true && !consentChecked;

  const choose = useCallback(async (candidate: File) => {
    const check = checkFootageFile(candidate);
    if (!check.ok) {
      setPhase('error');
      setMessage(check.reason);
      return;
    }
    setFile(candidate);
    setMessage(null);
    setPhase('probing');
    try {
      const probed = await probeVideoMetadata(candidate);
      setMetadata(probed);
      setPhase('idle');
    } catch (error) {
      setMetadata(null);
      setPhase('error');
      setMessage(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const startUpload = useCallback(async () => {
    if (!file) return;
    if (containsMinors === null) {
      setMessage('Answer the safeguarding question before uploading.');
      return;
    }
    if (gateBlocked) {
      setMessage('Consent is required before this footage can be stored.');
      return;
    }

    setPhase('uploading');
    setMessage(null);
    setLoaded(0);
    setTotal(file.size);

    try {
      const token = await getToken();
      const meta = {
        filename: file.name,
        durationSeconds: metadata?.durationSeconds ?? null,
        resolution: metadata?.resolution ?? null,
        codec: metadata?.codec ?? null,
        sizeBytes: file.size,
        containsMinors,
        consentGranted: consentChecked,
      };
      // Two paths, one progress bar: files past the Worker request-body ceiling are chunked
      // through the R2 multipart flow; everything else keeps the single-shot POST.
      const { promise } =
        planFootageUpload(file).mode === 'multipart'
          ? uploadFootageMultipart(file, meta, {
              token,
              onProgress: (l, t) => {
                setLoaded(l);
                setTotal(t);
              },
            })
          : uploadFootage(file, meta, {
              token,
              onProgress: (l, t) => {
                setLoaded(l);
                setTotal(t);
              },
            });
      const result = await promise;
      setFootage(result.footage);
      setPhase('ready');
      onUploaded(result.footage, result.media);
    } catch (error) {
      setPhase('error');
      setMessage(error instanceof Error ? error.message : String(error));
    }
  }, [consentChecked, containsMinors, file, gateBlocked, getToken, metadata, onUploaded]);

  return (
    <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-semibold tracking-tight">Footage</h2>

      <div
        data-testid="footage-dropzone"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const dropped = event.dataTransfer.files?.[0];
          if (dropped) void choose(dropped);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          dragging ? 'border-copper bg-copper-300/20' : 'border-chalk-200 bg-chalk/60'
        }`}
      >
        <p className="text-sm">Drag a match file here, or</p>
        <button
          type="button"
          data-testid="footage-pick"
          onClick={() => inputRef.current?.click()}
          className="rounded bg-copper px-3 py-1.5 text-sm font-medium text-charcoal"
        >
          Choose footage
        </button>
        <p className="text-xs text-charcoal-500">
          MP4 / H.264 · up to {formatBytes(FOOTAGE_MAX_BYTES)} per upload
        </p>
        <input
          ref={inputRef}
          data-testid="footage-input"
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.m4v,.mov"
          className="hidden"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked) void choose(picked);
          }}
        />
      </div>

      {file && (
        <dl data-testid="footage-metadata" className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-charcoal-500">File</dt>
          <dd className="truncate font-medium" data-testid="footage-filename">
            {file.name}
          </dd>
          <dt className="text-charcoal-500">Duration</dt>
          <dd data-testid="footage-duration">
            {metadata ? formatClock(metadata.durationSeconds) : 'reading…'}
          </dd>
          <dt className="text-charcoal-500">Resolution</dt>
          <dd data-testid="footage-resolution">{metadata?.resolution ?? 'reading…'}</dd>
          <dt className="text-charcoal-500">Size</dt>
          <dd>{formatBytes(file.size)}</dd>
        </dl>
      )}

      {/* Safeguarding gate — plan §3.3.1 */}
      <fieldset className="mt-4 rounded-lg border border-chalk-200 p-3">
        <legend className="px-1 text-sm font-medium">Safeguarding</legend>
        <p className="text-sm" data-testid="consent-question">
          Does this footage contain players under 18?
        </p>
        <div className="mt-2 flex gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="minors"
              data-testid="consent-minors-yes"
              checked={containsMinors === true}
              onChange={() => setContainsMinors(true)}
            />
            Yes
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="minors"
              data-testid="consent-minors-no"
              checked={containsMinors === false}
              onChange={() => setContainsMinors(false)}
            />
            No
          </label>
        </div>

        {containsMinors === true && (
          <label className="mt-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="consent-checkbox"
              checked={consentChecked}
              onChange={(event) => setConsentChecked(event.target.checked)}
            />
            <span>
              I confirm I have consent to store and process this footage. Consent is logged with a
              timestamp and the requesting IP.
            </span>
          </label>
        )}
      </fieldset>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="upload-button"
          disabled={!file || phase === 'uploading' || phase === 'probing'}
          onClick={() => void startUpload()}
          className="rounded bg-charcoal px-3 py-1.5 text-sm font-medium text-chalk disabled:opacity-40"
        >
          {phase === 'uploading' ? 'Uploading…' : 'Upload footage'}
        </button>
        <span data-testid="upload-phase" className="text-xs text-charcoal-500">
          {phase}
        </span>
      </div>

      {(phase === 'uploading' || phase === 'ready') && (
        <div className="mt-3">
          <div className="h-2 w-full overflow-hidden rounded bg-chalk-200">
            <div
              data-testid="upload-progress"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-full bg-copper transition-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-charcoal-500" data-testid="upload-percent">
            {percent}% · {formatBytes(loaded)} of {formatBytes(total)}
          </p>
        </div>
      )}

      {footage && (
        <p data-testid="footage-status" className="mt-3 text-sm">
          Status: <span className="font-mono">{footage.status}</span> · consent{' '}
          <span className="font-mono">{footage.consent_status}</span>
          {footage.retention_date ? ` · retained until ${footage.retention_date.slice(0, 10)}` : ''}
        </p>
      )}

      {message && (
        <p data-testid="upload-error" role="alert" className="mt-3 text-sm text-copper-600">
          {message}
        </p>
      )}
    </section>
  );
}
